import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApi } from "@/lib/api";
import { 
  ArrowLeft, 
  Check, 
  X, 
  AlertTriangle,
  Globe,
  FileCode,
  ChevronDown,
  ChevronUp,
  Edit2,
  Save,
  RotateCcw
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import NotesSection from "@/components/NotesSection";

export default function PlatformDetail() {
  const { platformId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const [platform, setPlatform] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingCapability, setEditingCapability] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchPlatform();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformId]);

  const fetchPlatform = async () => {
    try {
      // Try to get customized platform data first
      const response = await api.get(`/api/platform-customizations/platforms/${platformId}`);
      setPlatform(response.data);
    } catch (error) {
      // Fall back to base platform data
      try {
        const response = await api.get(`/api/platforms/${platformId}`);
        setPlatform(response.data);
      } catch (err) {
        console.error("Error fetching platform:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditCapability = (capability) => {
    setEditForm({
      category: capability.category,
      in_house: capability.in_house,
      description: capability.description,
      complexity: capability.complexity,
      outsource_opportunity: capability.outsource_opportunity
    });
    setEditingCapability(capability);
  };

  const handleSaveCapability = async () => {
    try {
      await api.patch(
        `/api/platform-customizations/platforms/${platformId}/capabilities/${editForm.category}`,
        editForm
      );
      toast.success("Capability updated");
      setEditingCapability(null);
      fetchPlatform();
    } catch (error) {
      console.error("Error updating capability:", error);
      toast.error("Failed to update capability");
    }
  };

  const handleResetCustomizations = async () => {
    if (!window.confirm("Reset all customizations for this platform?")) return;
    
    try {
      await api.delete(`/api/platform-customizations/platforms/${platformId}/customizations`);
      toast.success("Customizations reset");
      fetchPlatform();
    } catch (error) {
      toast.error("Failed to reset customizations");
    }
  };

  const getInitialColor = (name) => {
    const colors = {
      'A': 'bg-orange-100 text-orange-600',
      'T': 'bg-orange-100 text-orange-600', 
      'G': 'bg-blue-100 text-blue-600'
    };
    return colors[name?.charAt(0).toUpperCase()] || 'bg-zinc-100 text-zinc-600';
  };

  if (loading) {
    return (
      <div className="content-wrapper flex items-center justify-center min-h-[50vh]">
        <div className="loading-spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  if (!platform) {
    return (
      <div className="content-wrapper">
        <div className="text-center py-12">
          <h2 className="text-lg font-semibold text-zinc-900">Platform not found</h2>
          <button 
            onClick={() => navigate('/platforms')}
            className="btn-secondary mt-4"
          >
            Back to Platforms
          </button>
        </div>
      </div>
    );
  }

  const inHouseCapabilities = platform.capabilities.filter(c => c.in_house);
  const gapCapabilities = platform.capabilities.filter(c => !c.in_house);

  return (
    <div className="content-wrapper" data-testid="platform-detail-page">
      <button 
        onClick={() => navigate('/platforms')}
        className="flex items-center gap-1.5 text-zinc-600 hover:text-zinc-900 mb-4 transition-colors text-sm"
        data-testid="back-button"
      >
        <ArrowLeft size={16} />
        <span>Back to Platforms</span>
      </button>

      {/* Header - Compact */}
      <div className="card p-4 mb-4">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded flex items-center justify-center ${getInitialColor(platform.name)}`}>
            <span className="text-xl font-bold">{platform.name.charAt(0)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-zinc-900 mb-1">{platform.name}</h1>
            <p className="text-sm text-zinc-600 mb-2">{platform.description}</p>
            <div className="flex flex-wrap gap-3 text-xs">
              <div className="flex items-center gap-1.5">
                <Globe size={14} className="text-zinc-400" />
                <span className="text-zinc-600">
                  {platform.markets.length} markets: {platform.markets.join(', ')}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <FileCode size={14} className="text-zinc-400" />
                <span>
                  {platform.api_integration ? (
                    <span className="text-green-600 font-medium">API Available</span>
                  ) : (
                    <span className="text-zinc-500">Manual Upload</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Compliance Categories */}
        <div className="card p-4">
          <h2 className="text-sm font-semibold text-zinc-900 mb-3">Compliance Categories</h2>
          <Accordion type="multiple" className="space-y-1.5">
            {platform.compliance_categories.map((category) => (
              <AccordionItem 
                key={category.id} 
                value={category.id}
                className="border border-zinc-200 rounded-sm px-3"
              >
                <AccordionTrigger className="hover:no-underline py-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-zinc-900">{category.name}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-3">
                  <p className="text-xs text-zinc-600 mb-2">{category.description}</p>
                  
                  <div className="mb-2">
                    <div className="text-[10px] font-medium text-zinc-500 uppercase tracking-wide mb-1">
                      Requirements
                    </div>
                    <ul className="space-y-0.5">
                      {category.requirements.map((req, idx) => (
                        <li key={idx} className="flex items-start gap-1.5 text-xs text-zinc-700">
                          <span className="w-1 h-1 bg-violet-500 rounded-full mt-1.5 flex-shrink-0"></span>
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <div className="text-[10px] font-medium text-zinc-500 uppercase tracking-wide mb-1">
                      Data Fields
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {category.data_fields.map((field) => (
                        <code 
                          key={field}
                          className="px-1.5 py-0.5 bg-zinc-100 text-zinc-700 text-[10px] rounded font-mono"
                        >
                          {field}
                        </code>
                      ))}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Capabilities Analysis */}
        <div className="space-y-4">
          {/* In-House Capabilities */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <Check size={16} className="text-green-600" />
                <h2 className="text-sm font-semibold text-zinc-900">
                  In-House ({inHouseCapabilities.length})
                </h2>
              </div>
              {platform.has_customizations && (
                <Button variant="ghost" size="sm" onClick={handleResetCustomizations} title="Reset" className="h-7 w-7 p-0">
                  <RotateCcw className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {inHouseCapabilities.map((cap) => (
                <div 
                  key={cap.category}
                  className="p-2.5 bg-green-50 border border-green-200 rounded-sm group"
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="font-medium text-zinc-900 text-sm">
                      {cap.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <Badge variant="secondary" className="bg-green-100 text-green-700 text-[10px]">
                        {cap.complexity}
                      </Badge>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleEditCapability(cap)}
                        data-testid={`edit-cap-${cap.category}`}
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-zinc-600">{cap.description}</p>
                  {cap.outsource_opportunity !== 'none' && (
                    <p className="text-[10px] text-violet-600 mt-0.5">
                      Outsource: {cap.outsource_opportunity}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Gap Capabilities */}
          <div className="card p-4">
            <div className="flex items-center gap-1.5 mb-3">
              <AlertTriangle size={16} className="text-violet-600" />
              <h2 className="text-sm font-semibold text-zinc-900">
                Opportunities ({gapCapabilities.length})
              </h2>
            </div>
            <div className="space-y-2">
              {gapCapabilities.map((cap) => (
                <div 
                  key={cap.category}
                  className="p-2.5 bg-violet-50 border border-violet-200 rounded-sm group"
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="font-medium text-zinc-900 text-sm">
                      {cap.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <Badge 
                        className={`text-[10px] ${
                          cap.outsource_opportunity === 'full' 
                            ? 'bg-violet-600 text-white' 
                            : 'bg-violet-100 text-violet-700'
                        }`}
                      >
                        {cap.outsource_opportunity}
                      </Badge>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleEditCapability(cap)}
                        data-testid={`edit-cap-${cap.category}`}
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-zinc-600">{cap.description}</p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">
                    Complexity: {cap.complexity}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Notes Section */}
      <div className="mt-4">
        <NotesSection 
          entityType="platform" 
          entityId={platformId} 
          entityName={platform.name}
        />
      </div>

      {/* Report Types - Compact */}
      <div className="card p-4 mt-4">
        <h2 className="text-sm font-semibold text-zinc-900 mb-2">Available Report Types</h2>
        <div className="flex flex-wrap gap-1">
          {platform.report_types.map((report) => (
            <code 
              key={report}
              className="px-2 py-1 bg-zinc-100 text-zinc-700 text-xs rounded font-mono"
            >
              {report}
            </code>
          ))}
        </div>
      </div>

      {/* Action Buttons - Compact */}
      <div className="flex gap-3 mt-4">
        <button
          onClick={() => navigate('/gap-analysis')}
          className="btn-primary"
          data-testid="run-gap-analysis"
        >
          Run Gap Analysis
        </button>
        <button
          onClick={() => navigate('/pitch-generator')}
          className="btn-secondary"
          data-testid="generate-pitch"
        >
          Generate Pitch
        </button>
      </div>

      {/* Edit Capability Dialog */}
      <Dialog open={!!editingCapability} onOpenChange={() => setEditingCapability(null)}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Edit Capability</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Input 
                value={editForm.category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || ''} 
                disabled 
                className="bg-zinc-50"
              />
            </div>
            
            <div className="space-y-2">
              <Label>In-House Status</Label>
              <Select 
                value={editForm.in_house?.toString()} 
                onValueChange={(v) => setEditForm({...editForm, in_house: v === 'true'})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">In-House (Platform handles)</SelectItem>
                  <SelectItem value="false">Gap (Service opportunity)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Complexity</Label>
              <Select 
                value={editForm.complexity} 
                onValueChange={(v) => setEditForm({...editForm, complexity: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Outsource Opportunity</Label>
              <Select 
                value={editForm.outsource_opportunity} 
                onValueChange={(v) => setEditForm({...editForm, outsource_opportunity: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Input 
                value={editForm.description || ''} 
                onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                placeholder="Capability description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingCapability(null)}>Cancel</Button>
            <Button onClick={handleSaveCapability} data-testid="save-capability-btn">
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
